import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { financeiroAPI, inscricoesAPI } from '../services/api';
import { IconWallet } from '../components/icons';
import './Financeiro.css';

const fmtMoeda = (v) => `R$ ${parseFloat(v || 0).toFixed(2).replace('.', ',')}`;

const fmtDiasAtras = (data) => {
  const dias = Math.floor((Date.now() - new Date(data).getTime()) / (1000 * 60 * 60 * 24));
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'há 1 dia';
  return `há ${dias} dias`;
};

function Financeiro() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmandoId, setConfirmandoId] = useState(null);

  useEffect(() => {
    carregar();
  }, []);

  const carregar = async () => {
    try {
      const { data } = await financeiroAPI.resumo();
      setDados(data);
    } catch (err) {
      setError('Não foi possível carregar os dados financeiros.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async (id) => {
    setConfirmandoId(id);
    try {
      await inscricoesAPI.confirmar(id);
      await carregar();
    } catch (err) {
      setError('Erro ao confirmar pagamento. Tente novamente.');
    } finally {
      setConfirmandoId(null);
    }
  };

  if (loading) return <div className="fin-loading">Carregando dados financeiros...</div>;
  if (error && !dados) return <div className="error-message">{error}</div>;

  const { geral, porCampeonato, porFormaPagamento, pendentes } = dados;
  const confirmadas = parseInt(geral.confirmadas, 10) || 0;
  const receitaConfirmada = parseFloat(geral.receita_confirmada) || 0;
  const receitaPendente = parseFloat(geral.receita_pendente) || 0;
  const ticketMedio = confirmadas > 0 ? receitaConfirmada / confirmadas : 0;

  const maiorReceitaCampeonato = Math.max(1, ...porCampeonato.map((c) => Math.max(parseFloat(c.receita_confirmada), parseFloat(c.receita_pendente))));
  const totalFormaPagamento = porFormaPagamento.reduce((s, f) => s + parseFloat(f.total), 0) || 1;

  return (
    <div className="fin-container">
      <div className="fin-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><IconWallet /> Financeiro</h1>
          <p>Receita, pendências de pagamento e conciliação de todas as inscrições.</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="fin-kpi-row">
        <div className="fin-kpi fin-kpi-green">
          <span className="fin-kpi-label">Receita confirmada</span>
          <span className="fin-kpi-value">{fmtMoeda(receitaConfirmada)}</span>
          <span className="fin-kpi-sub">{confirmadas} inscrição{confirmadas === 1 ? '' : 'ões'} paga{confirmadas === 1 ? '' : 's'}</span>
        </div>
        <div className="fin-kpi fin-kpi-amber">
          <span className="fin-kpi-label">Receita pendente</span>
          <span className="fin-kpi-value">{fmtMoeda(receitaPendente)}</span>
          <span className="fin-kpi-sub">{geral.pendentes} aguardando confirmação</span>
        </div>
        <div className="fin-kpi">
          <span className="fin-kpi-label">Ticket médio</span>
          <span className="fin-kpi-value">{fmtMoeda(ticketMedio)}</span>
          <span className="fin-kpi-sub">por inscrição confirmada</span>
        </div>
        <div className="fin-kpi">
          <span className="fin-kpi-label">Canceladas</span>
          <span className="fin-kpi-value">{geral.canceladas}</span>
          <span className="fin-kpi-sub">inscrições canceladas</span>
        </div>
      </div>

      <div className="fin-grid-2">
        <div className="card fin-card">
          <h3>Receita por campeonato</h3>
          {porCampeonato.length === 0 && <p className="fin-empty-hint">Nenhuma inscrição registrada ainda.</p>}
          <div className="fin-bar-list">
            {porCampeonato.map((c) => (
              <div className="fin-bar-row" key={c.id} onClick={() => navigate('/campeonatos')} title="Ver campeonatos">
                <div className="fin-bar-label">
                  <span>{c.nome}</span>
                  <b>{fmtMoeda(c.receita_confirmada)}</b>
                </div>
                <div className="fin-bar-track">
                  <div className="fin-bar-fill fin-bar-confirmada" style={{ width: `${(parseFloat(c.receita_confirmada) / maiorReceitaCampeonato) * 100}%` }} />
                  {parseFloat(c.receita_pendente) > 0 && (
                    <div
                      className="fin-bar-fill fin-bar-pendente"
                      style={{
                        width: `${(parseFloat(c.receita_pendente) / maiorReceitaCampeonato) * 100}%`,
                        left: `${(parseFloat(c.receita_confirmada) / maiorReceitaCampeonato) * 100}%`,
                      }}
                    />
                  )}
                </div>
                <div className="fin-bar-meta">
                  <span>{c.confirmadas} confirmada{c.confirmadas === '1' ? '' : 's'}</span>
                  {parseInt(c.pendentes, 10) > 0 && <span className="fin-meta-amber">{c.pendentes} pendente{c.pendentes === '1' ? '' : 's'}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card fin-card">
          <h3>Formas de pagamento</h3>
          {porFormaPagamento.length === 0 && <p className="fin-empty-hint">Nenhum pagamento confirmado ainda.</p>}
          <div className="fin-forma-list">
            {porFormaPagamento.map((f, i) => {
              const pct = (parseFloat(f.total) / totalFormaPagamento) * 100;
              return (
                <div className="fin-forma-row" key={i}>
                  <div className="fin-forma-label">
                    <span>{f.forma}</span>
                    <b>{fmtMoeda(f.total)}</b>
                  </div>
                  <div className="fin-bar-track fin-bar-track-sm">
                    <div className="fin-bar-fill fin-bar-confirmada" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="fin-forma-qtd">{f.quantidade}x · {pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card fin-card">
        <h3>Pagamentos pendentes {pendentes.length > 0 && <span className="fin-count-badge">{pendentes.length}</span>}</h3>

        {pendentes.length === 0 && (
          <div className="empty-state">
            <h4>Nenhum pagamento pendente</h4>
            <p>Todas as inscrições estão confirmadas ou canceladas.</p>
          </div>
        )}

        {pendentes.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Campeonato</th>
                  <th>Atleta</th>
                  <th>Responsável</th>
                  <th>Valor</th>
                  <th>Aguardando</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendentes.map((p) => (
                  <tr key={p.id}>
                    <td>{p.campeonato_nome}</td>
                    <td>{p.aluno_nome}</td>
                    <td>{p.usuario_nome}<br /><small>{p.usuario_email}</small></td>
                    <td>{fmtMoeda(p.valor_total)}</td>
                    <td>{fmtDiasAtras(p.created_at)}</td>
                    <td>
                      <button
                        className="btn-primary"
                        style={{ padding: '6px 12px', fontSize: 12.5 }}
                        onClick={() => handleConfirmar(p.id)}
                        disabled={confirmandoId === p.id}
                      >
                        {confirmandoId === p.id ? 'Confirmando…' : 'Confirmar pagamento'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Financeiro;
